import type { ButtonHTMLAttributes, ReactNode, SVGProps } from 'react';
import {
  Activity,
  Archive,
  ArrowLeft,
  ArrowRight,
  ChartNoAxesCombined,
  Bell,
  Building2,
  CalendarCheck2,
  CalendarDays,
  CalendarPlus,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleDollarSign,
  CircleHelp,
  ClipboardCheck,
  Clock3,
  Copy,
  CreditCard,
  Download,
  FileClock,
  FileStack,
  FileText,
  Filter,
  Flag,
  HeartPulse,
  Home,
  Inbox,
  ListChecks,
  Landmark,
  Grid2X2,
  Mail,
  MapPin,
  Mic,
  Menu,
  MessageSquare,
  MoreHorizontal,
  PanelRightOpen,
  PauseCircle,
  Pencil,
  Phone,
  Plus,
  Printer,
  RefreshCw,
  Reply,
  Search,
  Save,
  Send,
  Settings,
  Shield,
  ShieldCheck,
  Stethoscope,
  SunMoon,
  Trash2,
  Upload,
  UserCheck,
  UserPlus,
  UserRound,
  Users,
  WalletCards,
  X,
  ExternalLink,
  Eye,
  EyeOff,
  LockKeyhole,
  LogOut,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { Link } from './router';
import './design-system/c194-shell-polish.css';

export type AppIconName =
  | 'activity' | 'arrow-left' | 'arrow-right' | 'billing' | 'insurance' | 'building' | 'calendar' | 'chevron-down' | 'chevron-left'
  | 'close' | 'copy' | 'edit' | 'external-link' | 'forms' | 'home' | 'locations'
  | 'mail' | 'menu' | 'patients' | 'providers' | 'security' | 'settings' | 'staff'
  | 'subscription' | 'support' | 'theme' | 'user' | 'users' | 'inbox' | 'bell' | 'waitlist' | 'template' | 'send' | 'save' | 'search' | 'pause' | 'trash' | 'check' | 'archive' | 'refresh' | 'upload'
  | 'clock' | 'wallet' | 'bank' | 'plaid' | 'more' | 'plus' | 'filter' | 'chevron-right' | 'clipboard-check' | 'message' | 'reports' | 'user-check'
  | 'mic' | 'dollar' | 'calendar-check' | 'calendar-plus' | 'alert' | 'flag' | 'file-clock' | 'panel' | 'heart' | 'phone' | 'eye' | 'eye-off' | 'lock' | 'log-out' | 'sparkles' | 'download' | 'reply' | 'printer';

const appIconMap: Record<AppIconName, LucideIcon> = {
  activity: Activity,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  billing: CreditCard,
  insurance: ShieldCheck,
  building: Building2,
  calendar: CalendarDays,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  close: X,
  copy: Copy,
  edit: Pencil,
  'external-link': ExternalLink,
  forms: FileText,
  home: Home,
  locations: MapPin,
  mail: Mail,
  menu: Menu,
  patients: UserPlus,
  providers: Stethoscope,
  security: Shield,
  settings: Settings,
  staff: Users,
  subscription: WalletCards,
  download: Download,
  support: CircleHelp,
  theme: SunMoon,
  user: UserRound,
  users: Users,
  inbox: Inbox,
  bell: Bell,
  waitlist: ListChecks,
  template: FileStack,
  send: Send,
  save: Save,
  search: Search,
  pause: PauseCircle,
  trash: Trash2,
  check: CheckCircle2,
  archive: Archive,
  refresh: RefreshCw,
  upload: Upload,
  clock: Clock3,
  wallet: WalletCards,
  bank: Landmark,
  plaid: Grid2X2,
  more: MoreHorizontal,
  plus: Plus,
  printer: Printer,
  filter: Filter,
  'chevron-right': ChevronRight,
  'clipboard-check': ClipboardCheck,
  message: MessageSquare,
  reports: ChartNoAxesCombined,
  'user-check': UserCheck,
  dollar: CircleDollarSign,
  'calendar-check': CalendarCheck2,
  'calendar-plus': CalendarPlus,
  alert: CircleAlert,
  flag: Flag,
  'file-clock': FileClock,
  panel: PanelRightOpen,
  heart: HeartPulse,
  phone: Phone,
  mic: Mic,
  eye: Eye,
  'eye-off': EyeOff,
  lock: LockKeyhole,
  'log-out': LogOut,
  sparkles: Sparkles,
  reply: Reply,
};

export function AppIcon({ name, className = '', size = 18, strokeWidth = 1.8, ...props }: { name: AppIconName; className?: string; size?: number; strokeWidth?: number } & Omit<SVGProps<SVGSVGElement>, 'name'>) {
  const Lucide = appIconMap[name];
  return <Lucide aria-hidden="true" size={size} strokeWidth={strokeWidth} className={`app-icon ${className}`.trim()} {...props} />;
}

export function WorkspaceTitle({ eyebrow, title, description, icon, actions }: { eyebrow?: ReactNode; title: ReactNode; description?: ReactNode; icon?: AppIconName; actions?: ReactNode }) {
  return <header className="app-workspace-title">
    <div className="app-workspace-title-main">
      {icon && <span className="app-workspace-title-icon"><AppIcon name={icon} size={22} /></span>}
      <div className="app-workspace-title-copy">
        {eyebrow && <div className="app-workspace-eyebrow" data-nds-type="subinfo">{eyebrow}</div>}
        <h1 data-nds-type="page-title">{title}</h1>
        {description && <p data-nds-type="body">{description}</p>}
      </div>
    </div>
    {actions && <div className="app-workspace-title-actions">{actions}</div>}
  </header>;
}

export function MetricTile({ icon, label, value, detail, to, onClick, tone = 'default' }: { icon: AppIconName; label: ReactNode; value: ReactNode; detail?: ReactNode; to?: string; onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick']; tone?: 'default' | 'success' | 'warning' | 'info' }) {
  const body = <>
    <span className={`app-metric-icon tone-${tone}`}><AppIcon name={icon} size={21} /></span>
    <span className="app-metric-copy"><small data-nds-type="subinfo">{label}</small><strong data-nds-type="metric-value">{value}</strong>{detail && <em data-nds-type="subinfo">{detail}</em>}</span>
  </>;
  if (to) return <Link className="app-metric-tile" to={to}>{body}</Link>;
  if (onClick) return <button type="button" className="app-metric-tile app-metric-button" onClick={onClick}>{body}</button>;
  return <div className="app-metric-tile">{body}</div>;
}

export function CompactMetricStrip({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`c229-metric-strip ${className}`.trim()}>{children}</div>;
}

export function PanelHeading({ icon, title, description, action }: { icon?: AppIconName; title: ReactNode; description?: ReactNode; action?: ReactNode }) {
  return <div className="app-panel-heading">
    <div className="app-panel-heading-copy">{icon && <span className="app-panel-heading-icon"><AppIcon name={icon} /></span>}<div><h2 data-nds-type="section-title">{title}</h2>{description && <p data-nds-type="subinfo">{description}</p>}</div></div>
    {action && <div className="app-panel-heading-action">{action}</div>}
  </div>;
}

export function ActionTile({ icon, title, description, to, primary = false, onClick, className = '' }: { icon: AppIconName; title: ReactNode; description?: ReactNode; to?: string; primary?: boolean; onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick']; className?: string }) {
  const content = <><span className="app-action-tile-icon"><AppIcon name={icon} size={20} /></span><span><strong data-nds-type="item">{title}</strong>{description && <small data-nds-type="subinfo">{description}</small>}</span></>;
  const classes = `app-action-tile ${primary ? 'primary' : ''} ${className}`.trim();
  return to ? <Link to={to} className={classes}>{content}</Link> : <button type="button" className={classes} onClick={onClick}>{content}</button>;
}

function identityTone(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash >>> 0) % 12;
}

export function AppAvatar({ name, size = 'md', identityKey }: { name: string; size?: 'sm' | 'md' | 'lg'; identityKey?: string }) {
  const initials = name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'N';
  const toneClass = identityKey ? ` app-avatar-identity app-avatar-tone-${identityTone(identityKey)}` : '';
  return <span className={`app-avatar app-avatar-${size}${toneClass}`}>{initials}</span>;
}
